import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createImpediment } from '@/api/business/impediment'
import { impedimentKeys } from '@/queries/useImpedimentQuery'
import Button from '@/components/Button' 
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    
} from '@/components/ui/form'
 
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'   
import { Input } from '@/components/ui/input'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
}

const AddDialog = ({ isOpen, onClose }: AddDialogProps) => {
    const queryClient = useQueryClient() 
    const { toast } = useToast() 

    const schema = z.object({ 
        DS_IMPEDITIVO: z.string().min(1, 'Necessário preencher o campo!'), 
    })

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {  
            DS_IMPEDITIVO: '', 
        },
    })
    
    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: createImpediment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: impedimentKeys.lists() })
            onClose()
            form.reset() 
            toast({
                title: 'Impedimento criado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar um Impedimento. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar o Impedimento',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => { 
        mutate( data.DS_IMPEDITIVO )
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[400px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Adicionar nova Operação</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    > 
                        <FormField
                            name="DS_IMPEDITIVO"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Impeditivo</FormLabel>
                                    <FormControl>
                                        <Input {...field} className='w-full bg-gray-600 text-sm rounded-md px-4 py-2 text-gray-50 outline-none focus:border-primary-500 border-none' /> 
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />  
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddDialog
